import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	ComponentController,
	filterNil,
	localeDateToDateTime,
} from '@lycoris-nubila/ngx-lycoris';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { nth, uniq } from 'lodash-es';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, combineLatest } from 'rxjs';
import {
	debounceTime,
	filter,
	map,
	pluck,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs/operators';

import { DocumentViewerDialogComponent } from 'dialog/document-viewer-dialog/document-viewer-dialog.component';
import { IDocument } from 'document/document';
import { IDocumentI18n } from 'document/document-i18n';
import { DOCUMENT_TYPES, DocumentType } from 'document/model/document.type';
import { DocumentApplicationPort } from 'document/port/document-application.port';
import { SortDirection } from 'infrastructure/common/model/api.sort-direction';
import { PhaseApplicationPort } from 'phase/port/phase-application.port';
import {
	HomeDocumentsFilterForm,
	HomeDocumentsPhase,
} from 'presentation/page/home/home-documents/home-documents.model';
import {
	CoacheeProfileDocumentsState,
	CoacheeProfileDocumentsStore,
} from 'presentation/page/profile/coachee-profile/coachee-profile-documents/coachee-profile-documents.store';
import { CoacheeProfileController } from 'presentation/page/profile/coachee-profile/coachee-profile.controller';

@Injectable()
@UntilDestroy()
export class CoacheeProfileDocumentsController extends ComponentController<CoacheeProfileDocumentsState> {
	public i18n =
		'presentation.page.profile.coachee-profile.coachee-profile-documents';

	public filterForm: FormGroup<HomeDocumentsFilterForm>;

	public dateSortCssIconClass$: Observable<string>;
	public documentTypeOptions$: Observable<SelectItem[]>;
	public documents$: Observable<IDocument[]>;
	public sectionOptions$: Observable<SelectItemGroup[]>;
	public isHandsetLandscape$!: Observable<boolean>;
	public showSectionColumn$: Observable<boolean>;
	public showTypeColumn$: Observable<boolean>;

	constructor(
		store: CoacheeProfileDocumentsStore,
		private _http: HttpClient,
		private _dialogService: DialogService,
		private _translateService: TranslateService,
		private _breakpointObserver: BreakpointObserver,
		private _phaseApplicationPort: PhaseApplicationPort,
		private _documentApplicationPort: DocumentApplicationPort,
		private _coacheeProfileController: CoacheeProfileController
	) {
		super(store);

		this.filterForm = new FormGroup<HomeDocumentsFilterForm>({
			dateSort: new FormControl<SortDirection>('DESC'),
			types: new FormControl<DocumentType[]>(),
			sectionsId: new FormControl<string[]>(),
			name: new FormControl<string>(),
			dates: new FormControl<Date[]>(),
		});

		this._initObservables();
		this._initListeners();
	}

	public toggleDateSort() {
		this.filterForm.patchValue({
			dateSort: this.filterForm.value.dateSort === 'DESC' ? 'ASC' : 'DESC',
		});
	}

	private _initObservables() {
		this.isHandsetLandscape$ = this._breakpointObserver
			.observe(Breakpoints.HandsetLandscape)
			.pipe(
				map((result) => result.matches),
				shareReplay({ refCount: true, bufferSize: 1 })
			);

		this.showSectionColumn$ = this._breakpointObserver
			.observe([Breakpoints.XLarge, Breakpoints.Large, Breakpoints.Medium])
			.pipe(
				map((result) => result.matches),
				shareReplay({
					bufferSize: 1,
					refCount: true,
				})
			);

		this.showTypeColumn$ = this._breakpointObserver
			.observe([Breakpoints.XLarge, Breakpoints.Large, Breakpoints.Medium])
			.pipe(
				map((result) => result.matches),
				shareReplay({
					bufferSize: 1,
					refCount: true,
				})
			);

		const phases$ = this._coacheeProfileController.coachee$.pipe(
			filterNil(),
			pluck('id'),
			switchMap((userId) =>
				this._phaseApplicationPort.observePhasesByUserId(userId).pipe(
					pluck('data'),
					filterNil(),
					switchMap((phases) =>
						combineLatest(
							phases.map((phase) =>
								phase.localization$.pipe(
									map(
										(loc) =>
											({
												sections$: phase.sections$,
												name: loc.name,
												id: phase.id,
											} as HomeDocumentsPhase)
									)
								)
							)
						)
					)
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.sectionOptions$ = phases$.pipe(
			switchMap((phases) =>
				combineLatest(
					phases.map((phase) =>
						phase.sections$.pipe(
							switchMap((sections) =>
								combineLatest(
									sections.map((section) =>
										section.localization$.pipe(
											map(
												(loc) =>
													({
														value: section.id,
														label: loc.name,
													} as SelectItem)
											)
										)
									)
								)
							),
							map(
								(sections) =>
									({
										items: sections,
										label: phase.name,
										value: phase.id,
									} as SelectItemGroup)
							)
						)
					)
				)
			)
		);

		this.dateSortCssIconClass$ = this.filterForm.value$.pipe(
			map((value) =>
				value.dateSort === 'DESC'
					? 'fak fa-str-arrow-down-wide-short'
					: 'fak fa-str-arrow-up-short-wide'
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.documentTypeOptions$ = combineLatest(
			DOCUMENT_TYPES.filter(
				(type) =>
					type != 'ANSWERS_BASIC_ASSESSMENT' && type != 'ANSWERS_DETAILED_ASSESSMENT'
			).map((type) =>
				this._translateService
					.stream(this.i18n + '.document.types.' + type)
					.pipe(map((label) => ({ value: type, label } as SelectItem)))
			)
		).pipe(shareReplay({ bufferSize: 1, refCount: true }));

		this.documents$ = this.select('documentsId').pipe(
			switchMap((documentsId) =>
				this._documentApplicationPort.observeDocuments(documentsId)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	private _initListeners() {
		this._coacheeProfileController.bottomReached$
			.pipe(
				filterNil(),
				filter((bottomReached) => bottomReached.activeTabIndex === 0),
				tap(() =>
					this.store.update((state) => ({
						documentsOffset: state.documentsId.length,
					}))
				)
			)
			.subscribe();

		this.filterForm.value$
			.pipe(
				debounceTime(250),
				tap(() =>
					this.store.update({
						documentsOffset: 0,
						reloadDocumentsTrigger: Date.now(),
					})
				),
				untilDestroyed(this)
			)
			.subscribe();

		combineLatest([
			this._coacheeProfileController.coachee$.pipe(filterNil()),
			this.select([
				'documentsOffset',
				'documentsChunkSize',
				'reloadDocumentsTrigger',
			]),
		])
			.pipe(
				switchMap(([coachee, data]) =>
					this._documentApplicationPort
						.fetchDocuments({
							updateStartTimestamp: nth(this.filterForm.value.dates, 0)
								? localeDateToDateTime(
										nth(this.filterForm.value.dates, 0),
										coachee.timeZone
								  )
										.startOf('day')
										.toMillis()
								: null,
							updateEndTimestamp: nth(this.filterForm.value.dates, 1)
								? localeDateToDateTime(
										nth(this.filterForm.value.dates, 1),
										coachee.timeZone
								  )
										.endOf('day')
										.toMillis()
								: null,
							name: this.filterForm.value.name,
							sortDirection: this.filterForm.value.dateSort || 'DESC',
							sectionsId: this.filterForm.value.sectionsId,
							types: this.filterForm.value.types,
							chunkSize: data.documentsChunkSize,
							offset: data.documentsOffset,
							sortProperty: 'UPDATE',
							userId: coachee.id,
						})
						.pipe(
							filter((result) => !(result instanceof Error)),
							tap((documentsId: string[]) =>
								data.documentsOffset === 0
									? this.store.update({ documentsId })
									: this.store.update((state) => ({
											documentsId: uniq([...state.documentsId, ...documentsId]),
									  }))
							)
						)
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	public downloadDocument(documentI18n: IDocumentI18n) {
		const name =
			documentI18n.url.includes('.docx') && !documentI18n.name.includes('.docx')
				? documentI18n.name + '.docx'
				: documentI18n.name;

		this._http
			.get(documentI18n.url, { responseType: 'blob' })
			.pipe(tap((blob) => saveAs(blob, name)))
			.subscribe();
	}

	public openDocument(documentI18n: IDocumentI18n) {
		this._dialogService.open(DocumentViewerDialogComponent, {
			data: { url: documentI18n.url },
			header: documentI18n.name,
			dismissableMask: true,
			width: '90%',
		});
	}

	public loadNextPage() {
		this.store.update((state) => ({
			documentsOffset: (state.documentsOffset ?? 0) + state.documentsChunkSize,
		}));
	}
}
